<template>
<div class="tk-col">
    <div class="card p-1 chat-card">
    <strong class="d-block mb-2">Comentarios</strong>

    <!-- Chat escrolleable -->
    <div ref="scrollEl" class="chat-scroll">
        <!-- Skeleton mientras carga la primera vez -->
        <template v-if="loadingChat">
        <div
            v-for="n in 4"
            :key="'sk-' + n"
            class="chat-item d-flex mb-2"
            :class="n % 2 === 0 ? 'chat-item--mine' : ''"
        >
            <div class="sk-avatar mr-1 flex-shrink-0"></div>
            <div class="chat-bubble sk-bubble">
            <div class="sk-line sk-line--meta"></div>
            <div class="sk-line sk-line--title"></div>
            <div class="sk-line"></div>
            <div class="sk-line sk-line--short"></div>
            </div>
        </div>
        </template>

        <!-- Comentarios reales -->
        <template v-else>
        <div
            v-for="c in localComments"
            :key="c.id"
            class="chat-item d-flex mb-2"
            :class="{ 'chat-item--mine': c.mine }"
        >
            <!-- Avatar -->
            <b-avatar
            square
            rounded="sm"
            :variant="c.mine ? 'light-primary' : 'light-danger'"
            class="chat-avatar flex-shrink-0"
            :class="c.mine ? 'ml-1' : 'mr-1'"
            :text="initialsFor(c.user)"
            />

            <!-- Burbuja -->
            <div class="chat-bubble" :class="c.mine ? 'chat-bubble--mine' : 'chat-bubble--other'">
            <div v-if="c.isCreation" class="chat-creation-tag mb-50">
                Creación del ticket
                <feather-icon icon="FileTextIcon" size="12" class="mr-25" />
            </div>
            <div class="chat-meta mb-1">
              <feather-icon icon="ClockIcon" size="12" class="mr-25" />
              <span class="text-primary">{{ fromNow(c.created_at) }}</span>
              <span class="mx-25">·</span>
              <feather-icon icon="UserIcon" size="12" class="mr-25" />
              <span class="font-weight-bold">{{ c.user }}</span>
            </div>
            <div v-if="c.body" class="chat-body">{{ c.body }}</div>
            <div v-if="c.evidences && c.evidences.length" class="chat-evidences mt-50">
                <!-- Imágenes: abren en un modal -->
                <button
                v-for="ev in c.evidences"
                v-if="isImage(ev)"
                :key="'img-' + ev.id"
                type="button"
                class="chat-evidence chat-evidence--img"
                :title="ev.original_name"
                @click="openImage(ev)"
                >
                <img :src="ev.url" :alt="ev.original_name" />
                </button>

                <!-- Otros archivos: se abren en pestaña nueva -->
                <a
                v-for="ev in c.evidences"
                v-if="!isImage(ev)"
                :key="'file-' + ev.id"
                :href="ev.download_url || ev.url"
                :download="ev.original_name"
                rel="noopener"
                class="chat-evidence"
                :title="ev.original_name"
                >
                <span><feather-icon icon="DownloadIcon" size="14" class="mr-25" />{{ ev.original_name }}</span>
                </a>
            </div>
            </div>
        </div>

        <div v-if="!localComments.length" class="text-muted small py-1 text-center">
            Sin comentarios
        </div>
        </template>
    </div>

    <!-- Input para mandar comentarios -->
    <div v-if="canComment" class="mt-1">
        <b-form-group label="Descripción / Nota" label-for="new-comment" class="mb-1">
        <counted-textarea
            id="new-comment"
            v-model="newComment"
            rows="3"
            max-rows="6"
            placeholder="Escribe un comentario..."
            :maxlength="textLimits.comment"
        />
        </b-form-group>

        <!-- Adjuntos -->
        <file-upload-with-preview
        id="ticket_attachments"
        :key="uploaderKey"
        name="attachments"
        :label="$t('helpdesk.ticket.attachments')"
        :multiple="true"
        :disabled="sendingComment"
        :uploading="sendingComment"
        :on-update-files="onUpdateFiles"
        />

        <div class="d-flex justify-content-end">
        <b-button variant="primary" :disabled="!canSendComment" @click="addComment">
            <b-spinner v-if="sendingComment" small class="mr-50" />
            Comentario
        </b-button>
        </div>
    </div>
    </div>

    <!-- Modal para ver la imagen en grande -->
    <b-modal
        :id="imageModalId"
        hide-footer
        centered
        size="lg"
        :title="previewImage ? previewImage.original_name : ''"
    >
        <div class="text-center">
        <img
            v-if="previewImage"
            :src="previewImage.url"
            :alt="previewImage.original_name"
            class="img-fluid preview-image"
        />
        </div>
        <div v-if="previewImage" class="d-flex justify-content-end mt-1">
        <b-button
            variant="outline-secondary"
            size="sm"
            :href="previewImage.url"
            target="_blank"
            rel="noopener"
        >
            <feather-icon icon="ExternalLinkIcon" size="14" class="mr-25" />
            Abrir original
        </b-button>
        </div>
    </b-modal>
</div>
</template>

<script>
import { BButton, BFormGroup, BAvatar, BSpinner, BModal } from "bootstrap-vue";
import moment from "moment-timezone";
import { ref, computed, nextTick, onMounted } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import FileUploadWithPreview from "@/components/BuildRiskForm/FileUploadWithPreview.vue";
import store from "@/store";
import CountedTextarea from "@/views/helpdesk/CountedTextarea.vue";
import { TEXT_LIMITS } from "@/views/helpdesk/textLimits";
import { getUserData } from "@/auth/utils";

export default {
name: "TicketChatColumn",
components: {
    BButton,
    BFormGroup,
    CountedTextarea,
    BAvatar,
    BSpinner,
    BModal,
    FileUploadWithPreview,
},
props: {
    ticket: { type: Object, required: true },
    ticketId: { type: [Number, String], required: true },
    // Comentarios iniciales que ya trae el padre (para pintar sin esperar)
    comments: { type: Array, default: () => [] },
    canComment: { type: Boolean, default: false },
    currentStatusCode: { type: String, default: null },
    // Si true, además de refrescar su propia lista, avisa al padre para
    // que sincronice historial/SLA. Por defecto NO recarga toda la vista.
    syncParent: { type: Boolean, default: false },
},
setup(props, { emit, root }) {
    const toast = useToast();

    const newComment = ref("");
    const commentFiles = ref([]);
    const uploaderKey = ref(0);
    const sendingComment = ref(false);
    const loadingChat = ref(false);
    const scrollEl = ref(null);

    // Usuario actual, para saber qué comentarios son "míos"
    const me = (() => {
    try {
        return getUserData() || {};
    } catch (e) {
        return {};
    }
    })();
    const myEmail = (me.email || "").toLowerCase();
    const myId = me.id;

    const isCommentEvent = (e) => !!(e.glpi_payload && e.glpi_payload.type === "comment");
    const eventUserName = (e) =>
    e.user ? [e.user.name, e.user.first_name].filter(Boolean).join(" ") || e.user.email : "—";
    const isMineEvent = (e) => {
    if (!e.user) return false;
    if (myId && e.user.id) return e.user.id === myId;
    if (myEmail && e.user.email) return e.user.email.toLowerCase() === myEmail;
    return false;
    };

    // Normaliza un comentario que ya viene "aplanado" (de la prop del padre)
    // añadiendo la marca mine si es posible.
    const markMineFromProp = (c) => ({
    ...c,
    mine: (() => {
        // el padre no manda user id/email; comparamos por nombre como fallback
        const myName = [me.name, me.first_name].filter(Boolean).join(" ") || me.name;
        return myName && c.user && c.user === myName;
    })(),
    });

    // Estado local de comentarios (arranca con lo que trae el padre)
    const localComments = ref((props.comments || []).map(markMineFromProp));

    const isImage = (ev) => /^image\//.test(ev.type || "");
    const fromNow = (value) => (value ? moment(value).fromNow() : "—");
    const initialsFor = (name) => (name ? name.trim().charAt(0).toUpperCase() : "?");

    // ── Modal de imagen ──
    const previewImage = ref(null);
    const imageModalId = `chat_image_modal_${props.ticketId}`;
    const openImage = (ev) => {
        previewImage.value = ev;
        root.$bvModal.show(imageModalId);
    };

    const scrollToBottom = () => {
    nextTick(() => {
        if (scrollEl.value) scrollEl.value.scrollTop = scrollEl.value.scrollHeight;
    });
    };

    const onUpdateFiles = (payload) => {
    commentFiles.value = payload.rawFiles || [];
    };

    const canSendComment = computed(
    () =>
        props.canComment &&
        props.currentStatusCode !== "CLOSED" &&
        !sendingComment.value &&
        !!props.ticket &&
        (!!newComment.value.trim() || commentFiles.value.length > 0)
    );

    // Recarga SOLO los comentarios de este ticket (sin tocar el resto de la vista)
    const reloadComments = (showSkeleton = false) => {
    if (showSkeleton) loadingChat.value = true;
    return store
        .dispatch("helpdesk/getTicket", { id: props.ticketId, params: {} })
        .then((response) => {
        const tk = (response && response.data && response.data.ticket) || null;
        const events = (tk && tk.events) || [];

        const list = events
            .filter(isCommentEvent)
            .map((e) => ({
            id: e.id,
            user: eventUserName(e),
            body: e.comment || "",
            evidences: e.evidences || [],
            created_at: e.created_at,
            mine: isMineEvent(e),
            }))
            .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

        // Primer "comentario": creación del ticket (mensaje + adjuntos de creación)
        if (tk) {
            const creationEvidences =
            tk.creation_evidences ||
            (events.find((e) => !isCommentEvent(e)) || {}).evidences ||
            [];
            list.unshift({
            id: `creation-${tk.id}`,
            user: eventUserName({ user: tk.user }),
            body: tk.message || tk.description || "",
            evidences: creationEvidences,
            created_at: tk.created_at,
            mine: isMineEvent({ user: tk.user }),
            isCreation: true,
            });
        }

        localComments.value = list;
        scrollToBottom();
        })
        .catch(() => {
        // si falla la recarga puntual, no rompemos la vista
        })
        .finally(() => {
        loadingChat.value = false;
        });
    };

    const addComment = () => {
    const formData = new FormData();
    const text = (newComment.value || "").trim();
    if (text) formData.append("message", text);
    commentFiles.value.forEach((file) => formData.append("attachments[]", file));

    sendingComment.value = true;
    store
        .dispatch("helpdesk/addTicketComment", { id: props.ticketId, params: formData })
        .then(() => {
        newComment.value = "";
        commentFiles.value = [];
        uploaderKey.value += 1;
        // Refresca SOLO el chat (skeleton mientras llega)
        reloadComments(true);
        // Opcional: avisar al padre para sincronizar historial/SLA
        if (props.syncParent) emit("reload");
        })
        .catch((error) => {
        toast({
            component: ToastificationContent,
            props: {
            title: error?.response?.data?.message || "No se pudo enviar el comentario.",
            icon: "AlertTriangleIcon",
            variant: "danger",
            },
        });
        })
        .finally(() => {
        sendingComment.value = false;
        });
    };

    onMounted(() => {
    scrollToBottom();
    });

    return {
    textLimits: TEXT_LIMITS,
    newComment,
    uploaderKey,
    sendingComment,
    loadingChat,
    localComments,
    canSendComment,
    addComment,
    onUpdateFiles,
    isImage,
    openImage,
    previewImage,
    imageModalId,
    fromNow,
    initialsFor,
    scrollEl,
    };
},
};
</script>

<style scoped>
/* Layout de columna */
.tk-col {
display: flex;
flex-direction: column;
gap: 12px;
min-height: 0;
}
.tk-col > .card {
margin-bottom: 0;
min-height: 0;
}

/* ── Chat ── */
.chat-card {
display: flex;
flex-direction: column;
flex: 1 1 auto;
min-height: 0;
background: #f9fafb;
border: 1px solid #eceff3;
border-radius: 8px;
}
.chat-scroll {
flex: 1 1 auto;
overflow-y: auto;
min-height: 0;
padding-right: 4px;
height: 320px;
max-height: 320px;
}
.chat-scroll::-webkit-scrollbar {
width: 6px;
}
.chat-scroll::-webkit-scrollbar-thumb {
background: #cfd4da;
border-radius: 3px;
}

/* Fila del comentario: por defecto a la izquierda (otros) */
.chat-item {
align-items: flex-end;
}
/* Los míos: alineados a la derecha e invertidos (avatar a la derecha) */
.chat-item--mine {
flex-direction: row-reverse;
}

.chat-avatar {
width: 34px;
height: 34px;
}

/* Burbuja base */
.chat-bubble {
border-radius: 10px;
padding: 8px 10px;
min-width: 0;
max-width: 78%;
}
/* Otros (izquierda): verde claro */
.chat-bubble--other {
background: #ffffff;
border: 1px solid #e6e9ee;
border-bottom-left-radius: 2px;
}
/* Míos (derecha): estilo "enviado" */
.chat-bubble--mine {
background: #dcf8c6;
border: 1px solid #cdeeb0;
border-bottom-right-radius: 2px;
}
.chat-item--mine .chat-meta {
flex-direction: row-reverse;
}

.chat-meta {
display: inline-flex;
align-items: center;
flex-wrap: wrap;
gap: 2px;
font-size: 0.72rem;
color: #6e6b7b;
}
.chat-creation-tag {
display: flex;
align-items: center;
width: 100%;
font-size: 0.68rem;
font-weight: 600;
text-transform: uppercase;
letter-spacing: 0.3px;
color: #0f9d58;
}
.chat-item--mine .chat-creation-tag {
flex-direction: row-reverse;
text-align: right;
}
.chat-body {
font-size: 0.85rem;
color: #2c3e33;
word-break: break-word;
white-space: pre-wrap;
}
.chat-evidences {
display: flex;
flex-wrap: wrap;
gap: 6px;
}
.chat-evidence img {
width: 72px;
height: 72px;
object-fit: cover;
border-radius: 4px;
}
/* La miniatura de imagen es un <button>: lo dejamos como una imagen clickeable */
.chat-evidence--img {
padding: 0;
border: 0;
background: transparent;
cursor: zoom-in;
line-height: 0;
}
.preview-image {
max-height: 70vh;
border-radius: 6px;
}

/* ── Skeleton loading ── */
.sk-avatar {
width: 34px;
height: 34px;
border-radius: 6px;
background: #e9ecef;
animation: sk-pulse 1.2s ease-in-out infinite;
}
.sk-bubble {
max-width: 78%;
width: 60%;
background: #ffffff;
border: 1px solid #eceff3;
}
.chat-item--mine .sk-bubble {
background: #eef6e6;
}
.sk-line {
height: 10px;
border-radius: 4px;
background: #e9ecef;
margin: 6px 0;
animation: sk-pulse 1.2s ease-in-out infinite;
}
.sk-line--meta {
width: 45%;
height: 8px;
}
.sk-line--title {
width: 70%;
}
.sk-line--short {
width: 35%;
}
@keyframes sk-pulse {
0%, 100% { opacity: 1; }
50% { opacity: 0.45; }
}
</style>